import classNames from "classnames";
import { Icon } from "Components/Icons";
import React, { PropsWithChildren, ReactNode, useState, useCallback, useEffect, useRef } from "react";
import { AsyncTypeahead, AsyncTypeaheadProps, TypeaheadInputSingle, TypeaheadModel } from "react-bootstrap-typeahead";
import { Spinner } from "Components/ReactstrapSlim";

const ActiveIndexWatcher = ({ update }) => {
   useEffect(update);
   return null;
};

export function sizeClass<T extends TypeaheadModel>(prefix: string, size: TypeaheadFLProps<T>["size"]) {
   return size === "lg" || size === "large"
      ? prefix + "lg"
      : size === "sm" || size === "small"
         ? prefix + "sm"
         : null
}

function isIconRenderFunction<T extends TypeaheadModel>(prop: Icon | IconRenderFunction<T>): prop is IconRenderFunction<T> {
   return (prop as IconRenderFunction<T>).isIconRenderFunction === true;
}
//setOpen: React.Dispatch<React.SetStateAction<boolean | undefined>>, setOptions: React.Dispatch<React.SetStateAction<boolean | undefined>>
export interface IconRenderFunction<T extends TypeaheadModel> {
   (
      isShown: boolean,
      showOptions: (options: T[]) => void,
      hideOptions: () => void,
   ): ReactNode,
   isIconRenderFunction: true
}

export interface TypeaheadFLProps<T extends TypeaheadModel> extends AsyncTypeaheadProps<T> {
   icon?: Icon | IconRenderFunction<T>;
   invalid?: boolean;
   errorMessage?: string | undefined;
   wrapperClassName?: string;
   onInputSubmit?: (value: string) => void;
   inputValue?: string
}
export const TypeaheadFL = <T extends TypeaheadModel>({ icon, invalid, errorMessage, wrapperClassName, onInputSubmit, options, onChange, useCache, onKeyDown, onMenuToggle, inputValue, ...rest }: PropsWithChildren<TypeaheadFLProps<T>>) => {
   const [focus, setFocus] = useState(false);
   const [customOptions, setCustomOptions] = useState<T[] | undefined>();
   const [activeIndex, setActiveIndex] = useState<number>(-1);
   const [key, setKey] = useState(0);
   const [isOpen, setIsOpen] = useState(false);
   const ref = useRef<AsyncTypeahead<T>>(null);

   const onMenuToggleWrapped = (isMenuOpen: boolean) => {
      setIsOpen(isMenuOpen);
      if (onMenuToggle)
         onMenuToggle(isMenuOpen);
   }

   const showOptions = (options: T[]) => {
      setCustomOptions(options);
      if (!isOpen)
         ref.current?.toggleMenu();
   }
   const hideOptions = () => {
      setCustomOptions(undefined);
      ref.current?.hideMenu();
   }

   const getOptions = () => {
      return customOptions !== undefined ? customOptions : options;
   }

   const onKeyDownWrapper = useCallback(
      (e) => {
         // Check whether the 'enter' key was pressed, and also make sure that
         // no menu items are highlighted.
         if (e.keyCode === 13 && activeIndex === -1) {
            if (onInputSubmit)
               onInputSubmit(e.target.value);

            if (ref.current)
               ref.current.hideMenu();
         }
         if (onKeyDown)
            onKeyDown(e);
      },
      [activeIndex]
   );

   useEffect(() => {
      if (inputValue !== undefined)
         setKey(key + 1);
   }, [inputValue])

   return (
      <AsyncTypeahead<T>
         {...rest}
         ref={ref}
         key={key}
         options={getOptions()}
         useCache={customOptions === undefined ? useCache : false}
         onChange={selectedOptions => {
            hideOptions();
            if (onChange)
               onChange(selectedOptions);
         }}
         onKeyDown={onKeyDownWrapper}
         defaultInputValue={inputValue}
         onMenuToggle={onMenuToggleWrapped}
         renderInput={(inputProps, typeaheadState) => {
            const commonProps = {
               ...inputProps,
               isInvalid: rest.isInvalid,
               isValid: rest.isValid,
            };
            return (
               <div className={classNames("rfl", { "rfl--hasvalue": !!inputProps.value, "rfl--hasfocus": focus, "rfl--invalid": invalid, "rfl--disabled": rest.disabled }, sizeClass("rfl-", rest.size), wrapperClassName)}>
                  <TypeaheadInputSingle
                     {...commonProps}
                     placeholder={undefined}
                     aria-label={inputProps.placeholder}
                     className={classNames(inputProps.className, "rfl-input", { "rfl-input--hasicon": rest.isLoading || icon != null, "is-invalid": invalid }, sizeClass("form-control-", rest.size))}
                     onFocus={(e) => { setFocus(true); hideOptions(); inputProps.onFocus(e); }}
                     onBlur={(e) => { setFocus(false); inputProps.onBlur(e); }}
                  />
                  {
                     !errorMessage
                        ? <label htmlFor={inputProps.name} className="rfl-label">{inputProps.placeholder}</label>
                        : null
                  }

                  <div className="rfl-icon">
                     {
                        rest.isLoading
                           ? <Spinner size="sm" />
                           : icon
                              ? isIconRenderFunction(icon)
                                 ? icon(
                                    customOptions !== undefined && (typeaheadState as any).isMenuShown,
                                    showOptions,
                                    hideOptions
                                 )
                                 : React.createElement(icon, {})
                              : null
                     }
                  </div>
                  {
                     errorMessage
                        ? <div role="alert" className="rfl-valdation-message">
                           {errorMessage}
                        </div>
                        : null
                  }
               </div>
            )
         }}
      >
         {(state) => (
            // Passing a child render function to the component exposes partial
            // internal state, including the index of the highlighted menu item.
            <ActiveIndexWatcher update={() => setActiveIndex(state.activeIndex)} />
         )}
      </AsyncTypeahead>
   );
}
